import { isAddress } from "viem";
import { z } from "zod";

/**
 * Validates a simple chain identifier (non-CAIP-2)
 * Examples: "1", "42161", "cosmoshub-4"
 */
const validateChainId = (value: string): boolean => {
  return /^[a-zA-Z0-9_-]+$/.test(value);
};

/**
 * Validates Ethereum address checksum format
 * Uses EIP-55 checksum validation via viem
 */
const validateEthereumAddress = (address: string): boolean => {
  try {
    // viem isAddress function validates both format and EIP-55 checksum
    return isAddress(address);
  } catch {
    return false;
  }
};

/**
 * Zod schema for chain identifier (non-CAIP-2)
 */
const ChainIdSchema = z.string().refine(validateChainId, {
  message: "Must be a valid chain identifier (e.g., '1', '42161', 'cosmoshub-4')",
});

/**
 * Zod schema for Ethereum address with checksum validation
 */
const EthereumAddressSchema = z.string().refine(validateEthereumAddress, {
  message: "Must be a valid Ethereum address with proper EIP-55 checksum",
});

/**
 * Zod schema for protocol configuration
 */
export const LendBorrowMarkets = z.array(
  z.enum([
    "1200", // Aave V3 Ethereum
    "1202", // Aave V3 Testnet Sepolia
    "1302", // Compound V3 Testnet
    "1003", // Aave V2 Avalanche
    "1203", // Aave V3 Avalanche
    "1005", // Aave V2 Polygon
    "1205", // Aave V3 Polygon
    "1204", // Aave V3 Arbitrum
    "1206", // Aave V3 Base
    "1402", // Morpho Base
    "1207", // Aave V3 Optimism
  ]),
);

// Assets and chains use simple chain IDs (non-CAIP-2)

/**
 * Zod schema for widget features configuration
 */
/**
 * Base feature toggle schema: always an object with at least { enabled: boolean }.
 * Use loose() to allow forward-compatible extra fields without breaking validation.
 */
const BaseFeatureSchema = z
  .object({
    enabled: z.boolean(),
  })
  .loose();

/**
 * Asset configuration with chain ID and address
 */
const AssetConfigSchema = z.object({
  chainId: ChainIdSchema,
  address: EthereumAddressSchema,
});

/**
 * Shared schema for allow/block list filters
 */
const AllowBlockListSchema = z.object({
  allowList: z.array(z.string()).default([]),
  blockList: z.array(z.string()).default([]),
});

/**
 * Chains allow/block lists for simple chain IDs
 */
const ChainAllowBlockListSchema = z.object({
  allowList: z.array(ChainIdSchema).default([]),
  blockList: z.array(ChainIdSchema).default([]),
});

/**
 * Asset-specific schema for allow/block list filters with chainId and address
 */
const AssetAllowBlockListSchema = z.object({
  allowList: z.array(AssetConfigSchema).default([]),
  blockList: z.array(AssetConfigSchema).default([]),
});

/**
 * Swap-specific feature schema extending the base feature with allow/block lists.
 */
const SwapFeatureSchema = BaseFeatureSchema.extend({
  chains: ChainAllowBlockListSchema.default({ allowList: [], blockList: [] }),
  assets: AssetAllowBlockListSchema.default({ allowList: [], blockList: [] }),
  dexes: AllowBlockListSchema.default({ allowList: [], blockList: [] }),
  bridges: AllowBlockListSchema.default({ allowList: [], blockList: [] }),
}).loose();

/**
 * DeFi-specific feature schema extending the base feature with allow/block lists
 * for chains and assets.
 */
const DefiFeatureSchema = BaseFeatureSchema.extend({
  // Optional protocol markets for DeFi
  markets: LendBorrowMarkets.optional(),
  // Chains allow/block lists using simple chain IDs
  chains: ChainAllowBlockListSchema.default({ allowList: [], blockList: [] }),
  // Assets allow/block lists using simple chain IDs and checksummed addresses
  assets: AssetAllowBlockListSchema.default({ allowList: [], blockList: [] }),
  customVaults: z.boolean().optional(),
}).loose();

export const WidgetFeaturesSchema = z.object({
  portfolio: BaseFeatureSchema,
  rewards: BaseFeatureSchema,
  swap: SwapFeatureSchema,
  defi: DefiFeatureSchema,
  discover: BaseFeatureSchema,
  walletInfo: z.boolean().default(true),
  walletMode: z.enum(["internal", "external"]),
});

/**
 * Logo configuration - supports either a single URL or separate light/dark logos
 */
const LogoConfigSchema = z.union([
  // Single logo URL for both themes
  z
    .string()
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true;
        try {
          new URL(val);
          return true;
        } catch {
          return val.startsWith("/");
        }
      },
      { message: "Must be a valid URL or relative path starting with /" },
    ),
  // Separate logos for light and dark themes
  z.object({
    lightLogo: z.string().refine(
      (val) => {
        try {
          new URL(val);
          return true;
        } catch {
          return val.startsWith("/");
        }
      },
      { message: "Must be a valid URL or relative path starting with /" },
    ),
    darkLogo: z.string().refine(
      (val) => {
        try {
          new URL(val);
          return true;
        } catch {
          return val.startsWith("/");
        }
      },
      { message: "Must be a valid URL or relative path starting with /" },
    ),
  }),
]);

/**
 * Width configuration - supports either device types or custom pixel values
 */
const WidthSchema = z.union([
  z.enum(["mobile", "tablet"]), // Device types
  z
    .number()
    .min(320)
    .max(1000), // Custom pixel width
]);

/**
 * Height configuration - supports either device types or custom pixel values
 */
const HeightSchema = z.union([
  z.enum(["mobile", "tablet"]), // Device types
  z
    .number()
    .min(480)
    .max(1200), // Custom pixel height
]);

/**
 * Zod schema for widget appearance configuration
 *
 * Note: The theme setting can be overridden by customers when embedding the widget
 * by adding a 'colorScheme' URL parameter (e.g., ?colorScheme=dark)
 * This allows customers to set the color scheme without modifying the server configuration.
 */
export const WidgetAppearanceSchema = z.object({
  logoUrl: LogoConfigSchema.optional(),
  width: WidthSchema,
  height: HeightSchema.default("mobile"),
  theme: z.enum(["light", "dark"]).default("light"),
  builtByBlockdaemon: z.boolean().default(true),
  locale: z.string().optional(),
});

/**
 * Zod schema for protocol configuration
 */
export const DefiProtocolSchema = z.array(
  z.enum([
    "1200", // Aave V3 Ethereum
    "1202", // Aave V3 Testnet Sepolia
    "1302", // Compound V3 Testnet
    "1003", // Aave V2 Avalanche
    "1203", // Aave V3 Avalanche
    "1005", // Aave V2 Polygon
    "1205", // Aave V3 Polygon
    "1204", // Aave V3 Arbitrum
    "1206", // Aave V3 Base
    "1402", // Morpho Base
    "1207", // Aave V3 Optimism
  ]),
);

/**
 * Main widget configuration schema
 */
export const WidgetConfigSchema = z.object({
  features: WidgetFeaturesSchema,
  appearance: WidgetAppearanceSchema,
});

/**
 * Refined schema with additional business logic validation
 */
export const ValidatedWidgetConfigSchema = WidgetConfigSchema.refine(
  (config) => {
    // Ensure at least one core page feature is enabled (exclude walletMode and walletInfo)
    const enabledCoreFeatures = [
      config.features.portfolio.enabled,
      config.features.rewards.enabled,
      config.features.swap.enabled,
      config.features.defi.enabled,
      config.features.discover.enabled,
    ].filter(Boolean);
    return enabledCoreFeatures.length > 0;
  },
  {
    message: "At least one feature (portfolio, rewards, swap, defi, or discover) must be enabled",
    path: ["features"],
  },
);

// Export types inferred from schemas
export type WidgetFeatures = z.infer<typeof WidgetFeaturesSchema>;
export type WidgetAppearance = z.infer<typeof WidgetAppearanceSchema>;
export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;
export type ValidatedWidgetConfig = z.infer<typeof ValidatedWidgetConfigSchema>;

/**
 * Normalized feature flags for UI consumption: always booleans for feature pages,
 * while keeping wallet fields as-is. This allows the rest of the app to treat
 * features as simple flags even if the config uses object forms.
 */
export type NormalizedWidgetFeatures = {
  portfolio: boolean;
  rewards: boolean;
  swap: boolean;
  defi: boolean;
  discover: boolean;
  walletInfo: boolean;
  walletMode: "internal" | "external";
};

export function normalizeFeatures(features: WidgetFeatures): NormalizedWidgetFeatures {
  const get = (value: { enabled: boolean }): boolean => Boolean(value?.enabled);

  return {
    portfolio: get(features.portfolio),
    rewards: get(features.rewards),
    swap: get(features.swap),
    defi: get(features.defi),
    discover: get(features.discover),
    walletInfo: Boolean(features.walletInfo),
    walletMode: features.walletMode,
  };
}

/**
 * Validates a customer config object against the schema
 */
export function validateCustomerConfig(config: unknown): ValidatedWidgetConfig {
  try {
    return ValidatedWidgetConfigSchema.parse(config);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.issues
        .map((err) => {
          const path = err.path.join(".");
          return `${path}: ${err.message}`;
        })
        .join(", ");

      throw new Error(`Invalid customer config: ${errorMessages}`);
    }
    throw error;
  }
}

/**
 * Safely validates a config and returns result with error information
 */
export function safeValidateCustomerConfig(config: unknown): {
  success: boolean;
  data?: ValidatedWidgetConfig;
  error?: string;
} {
  try {
    const validatedConfig = validateCustomerConfig(config);
    return { success: true, data: validatedConfig };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown validation error",
    };
  }
}
